import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Check, X, ArrowRight } from "lucide-react";

export default function Comparison() {
  const { t } = useTranslation("landing");

  const rows = t("comparison.rows", { returnObjects: true }) as string[][] || [];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="bg-soft-grad py-24 relative overflow-hidden">
      {/* Background glow decoration */}
      <div className="pointer-events-none absolute top-1/2 left-1/4 h-[300px] w-[300px] -translate-y-1/2 rounded-full bg-[oklch(0.55_0.24_295/0.15)] blur-[100px]" />

      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          <p className="label-eyebrow">{t("comparison.tag")}</p>
          <h2 className="mt-3 text-4xl font-extrabold text-navy sm:text-[42px] tracking-tight">
            {t("comparison.title")}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-body font-light">
            {t("comparison.subtitle")}
          </p>
        </div>

        {/* Comparison Table Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto mt-16 rounded-[2rem] border border-slate-200/80 overflow-hidden bg-white shadow-soft"
        >
          <div className="grid grid-cols-2">
            {/* Header Column 1 */}
            <div className="bg-[#F8FAFC] py-5 px-6 text-center text-xs sm:text-sm font-bold text-slate-500 uppercase tracking-wider border-b border-slate-200 border-r border-slate-200 flex items-center justify-center">
              {t("comparison.othersLabel", "OTHERS")}
            </div>
            {/* Header Column 2 */}
            <div className="bg-navy py-5 px-6 text-center text-xs sm:text-sm font-bold text-white uppercase tracking-wider border-b border-slate-200 flex items-center justify-center">
              {t("comparison.tpaLabel", "AI IMPACT FELLOWSHIP")}
            </div>

            {/* Rows */}
            {rows.map(([a, b], i) => {
              const isLastRow = i === rows.length - 1;
              const rowBorderClass = isLastRow ? "" : "border-b border-slate-200/80";
              return (
                <React.Fragment key={i}>
                  {/* OTHERS COLUMN ROW CELL */}
                  <div className={`p-5 sm:p-6 flex items-center gap-3.5 border-r border-slate-200/80 ${rowBorderClass}`}>
                    <X className="h-5 w-5 text-rose-500 shrink-0" strokeWidth={2.5} />
                    <span className="text-sm sm:text-base text-slate-500 font-medium leading-relaxed">
                      {a}
                    </span>
                  </div>
                  {/* TPA COLUMN ROW CELL */}
                  <div className={`p-5 sm:p-6 flex items-center gap-3.5 ${rowBorderClass} bg-blue-bg/10`}>
                    <Check className="h-5 w-5 text-emerald-500 shrink-0" strokeWidth={2.5} />
                    <span className="text-sm sm:text-base text-navy font-semibold leading-relaxed">
                      {b}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        </motion.div>

        {/* Centered CTA Button */}
        <div className="mt-12 flex justify-center">
          <button
            onClick={() => scrollToSection("apply")}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-primary hover:bg-primary/95 text-white px-8 py-3.5 text-sm font-bold transition-all shadow-glow hover:scale-105 cursor-pointer border-0"
          >
            <span>{t("navbar.applyNow", "Apply Now")}</span>
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
